import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IGroup, Selection, Label } from '@fluentui/react';
import {
  Checkbox,
  Field,
  Input,
  FluentProvider,
  Spinner,
  webLightTheme,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  TableCellLayout,
  Button,
  makeStyles,
  TableSelectionCell,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';
import { Action, GET_ACTIONS_QUERY, RoleActions } from '../../utils/statApi/RolesApi';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import { useCollapsedGroups } from './useCollapsedGroups';

interface RoleActionsFormProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormMethods<Record<string, any>>;
  roleActions: RoleActions;
}

const useStyles = makeStyles({
  groupHeader: { display: 'flex', alignItems: 'center' },
});

const RoleActionsForm = ({ form, roleActions }: RoleActionsFormProps): JSX.Element => {
  const { control, errors, setValue, register } = form;
  const [actionGroups, setActionGroups] = useState<IGroup[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const selectionRef = useRef<Selection>();
  const { collapseGroups, toggleCollapseGroup } = useCollapsedGroups();

  if (!selectionRef.current) {
    selectionRef.current = new Selection({
      onSelectionChanged: () => {
        const selected = selectionRef.current?.getSelection() as Action[];
        setValue('actions', selected || []);
        setSelectionVersion((v) => v + 1); // force rerender for checked UI
      },
    });
  }
  const selectionObject = selectionRef.current;

  const styles = useStyles();
  const { data, loading, error } = useQuery(GET_ACTIONS_QUERY);

  const actions: Action[] = data?.actions;

  useEffect(() => {
    register('actions');
    setValue('actions', roleActions?.actions || []);
  }, []);

  const getCheckedState = (all: boolean, some: boolean): boolean | 'mixed' => {
    if (all) return true;
    if (some) return 'mixed';
    return false;
  };

  const getIndexById = useCallback(
    (id: string) => {
      return actions?.findIndex((a) => a.id.toString() === id) ?? -1;
    },
    [actions],
  );

  const isActionSelected = useCallback(
    (id: string) => {
      const index = getIndexById(id);
      return index >= 0 ? selectionObject.isIndexSelected(index) : false;
    },
    [getIndexById, selectionObject, selectionVersion],
  );

  const toggleRowSelection = useCallback(
    (id: string) => {
      const index = getIndexById(id);
      if (index < 0) return;
      const next = !selectionObject.isIndexSelected(index);
      selectionObject.setIndexSelected(index, next, false);
    },
    [getIndexById, selectionObject],
  );

  const getGroupItems = useCallback(
    (groupKey: string) => (actions || []).filter((a) => a.entityName === groupKey),
    [actions],
  );
  const getGroupSelectionState = useCallback(
    (groupKey: string) => {
      const items = getGroupItems(groupKey);
      if (!items.length) return { all: false, some: false };

      const selectedCount = items.reduce(
        (acc, i) => acc + (isActionSelected(i.id.toString()) ? 1 : 0),
        0,
      );

      return {
        all: selectedCount === items.length,
        some: selectedCount > 0 && selectedCount < items.length,
      };
    },
    [getGroupItems, isActionSelected],
  );

  const totalCount = actions?.length ?? 0;
  const selectedCount = selectionObject.getSelectedCount();

  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  const toggleAllRows = useCallback(() => {
    selectionObject.setAllSelected(!allSelected);
  }, [allSelected, selectionObject]);

  const toggleAllKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        toggleAllRows();
        e.preventDefault();
      }
    },
    [toggleAllRows],
  );

  const toggleGroupRows = useCallback(
    (groupKey: string) => {
      const items = getGroupItems(groupKey);
      const shouldSelect = !items.every((i) => isActionSelected(i.id.toString()));

      items.forEach((item) => {
        const index = getIndexById(item.id.toString());
        if (index >= 0) selectionObject.setIndexSelected(index, shouldSelect, false);
      });
    },
    [getGroupItems, getIndexById, isActionSelected, selectionObject],
  );

  const toggleGroupKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, groupKey: string) => {
      if (e.key === ' ' || e.key === 'Enter') {
        toggleGroupRows(groupKey);
        e.preventDefault();
      }
    },
    [toggleGroupRows],
  );

  useEffect(() => {
    if (!actions) return;

    const actionObject = actions?.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (acc: any, item: Action) => {
        if (!acc[item.entityName]) {
          acc[item.entityName] = [];
        }
        acc[item.entityName].push(item);
        return acc;
      },
      {},
    );

    const groups: IGroup[] = Object.keys(actionObject).map((propName) => {
      const startIndex = actions.findIndex((ac: Action) => ac.entityName === propName);
      return {
        key: propName,
        name: propName,
        startIndex,
        count: actionObject[propName].length,
        level: 0,
      };
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectionObject.setItems(actions as any, true);

    roleActions?.actions.forEach((act: Action) => {
      const index = actions.findIndex((a) => a.id === act.id);
      if (index >= 0) {
        selectionObject.setIndexSelected(index, true, false);
      }
    });
    setActionGroups(groups);
    setValue('actions', selectionObject.getSelection() as Action[]);
    setSelectionVersion((v) => v + 1);
  }, [actions, roleActions, selectionObject, setValue]);

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 20 }}>
        <input type="hidden" ref={register()} name="id" defaultValue={roleActions?.id} />
        <LoadingErrorMessage loading={loading} error={error} actionName="loading the actions" />
        <Controller
          control={control}
          name="role.name"
          defaultValue={roleActions?.role?.name || ''}
          rules={{
            required: 'Please enter Role name',
            maxLength: {
              value: 150,
              message: 'Input is greater than the maximum character limit of 150.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Name" required validationMessage={errors?.name?.message}>
              <Input
                id="name"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, inputData) => onChange(inputData.value)}
              />
            </Field>
          )}
        />

        <Controller
          name="role.isGlobal"
          control={control}
          defaultValue={roleActions?.role?.isGlobal ?? false}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Checkbox
              label="Is Global"
              onChange={(
                e: React.FormEvent<HTMLInputElement | HTMLElement>,
                checkedData: { checked: boolean },
              ): void => {
                const checked = checkedData?.checked === true;
                onChange(checked);
              }}
              checked={value ?? false}
              name={name}
            />
          )}
        />

        <Controller
          name="role.showInSecurityRolesList"
          control={control}
          defaultValue={roleActions?.role?.showInSecurityRolesList ?? false}
          render={({ onChange, onBlur, value, name }): React.ReactElement => (
            <Checkbox
              label="Show In Security Roles List"
              onChange={(
                e: React.FormEvent<HTMLInputElement | HTMLElement>,
                checkedData: { checked: boolean },
              ): void => {
                const checked = checkedData?.checked === true;
                onChange(checked);
              }}
              checked={value ?? false}
              name={name}
            />
          )}
        />

        <Label>Assigned Actions</Label>
        {!loading && (
          <Table aria-label="Assigned Actions table">
            <TableHeader>
              <TableRow>
                <TableSelectionCell
                  checked={getCheckedState(allSelected, someSelected)}
                  onClick={toggleAllRows}
                  onKeyDown={toggleAllKeydown}
                  checkboxIndicator={{ 'aria-label': 'Select all rows ' }}
                />
                <TableHeaderCell>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={collapseGroups.All ? <ChevronRightRegular /> : <ChevronDownRegular />}
                    onClick={() =>
                      toggleCollapseGroup(
                        'All',
                        actionGroups.map((g) => g.key),
                      )
                    }
                    aria-label={`${collapseGroups.All ? 'Expand' : 'Collapse'} All`}
                  />
                  <TableCellLayout> Entity Name</TableCellLayout>
                </TableHeaderCell>
                <TableHeaderCell>Entity Action</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actionGroups &&
                actionGroups.map((group) => {
                  const isCollapsed = !!collapseGroups[group.key];
                  const { all: groupFullySelected, some: groupPartiallySelected } =
                    getGroupSelectionState(group.key);

                  return (
                    <React.Fragment key={group.key}>
                      <TableRow>
                        <TableSelectionCell
                          checked={getCheckedState(groupFullySelected, groupPartiallySelected)}
                          onClick={() => toggleGroupRows(group.key)}
                          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                            toggleGroupKeydown(e, group.key)
                          }
                          checkboxIndicator={{ 'aria-label': `Select ${group.name} rows` }}
                        />
                        <TableCell>
                          <div className={styles.groupHeader}>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                              onClick={() => toggleCollapseGroup(group.key)}
                              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${group.name}`}
                            />
                            <TableCellLayout>
                              {group.name} ({group.count})
                            </TableCellLayout>
                          </div>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed &&
                        actions
                          .filter((item) => item.entityName === group.key)
                          .map((item) => {
                            return (
                              <TableRow key={item.id}>
                                <TableSelectionCell
                                  checked={isActionSelected(item.id.toString())}
                                  onClick={() => toggleRowSelection(item.id.toString())}
                                  checkboxIndicator={{
                                    'aria-label': `Select ${item.entityName} ${item.entityAction}`,
                                  }}
                                />
                                <TableCell>
                                  <TableCellLayout style={{ paddingLeft: '32px' }}>
                                    {item.entityName}
                                  </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                  <TableCellLayout>{item.entityAction}</TableCellLayout>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>
        )}
        {loading && <Spinner aria-label="Loading" />}
      </Stack>
    </FluentProvider>
  );
};

export default RoleActionsForm;
